import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganisationTag, OrganisationTagType } from '../../../../../services';
import {
  Button,
  ButtonType,
  FeedFollowBehavior,
  InputField,
  ORG_TAGS_NAME_LENGTH_LIMIT,
  Select,
} from '../../../../../shared';

import classes from './TagEdit.module.scss';

export interface TagEditResult {
  name: string;
  type: OrganisationTagType;
  feedFollowBehavior?: FeedFollowBehavior;
}

interface TagEditProps {
  onSubmit: (result: TagEditResult) => void;
  defaultType?: OrganisationTagType;
  tag?: OrganisationTag;
}

export const TagEdit: FunctionComponent<TagEditProps> = ({ onSubmit, defaultType, tag }) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'organisationTags' });

  const initialName = tag?.name ?? '';

  const initialFollowBehavior =
    tag?.settings?.feedFollowBehavior ?? FeedFollowBehavior.OPTIONAL_UNFOLLOW;

  const [name, setName] = useState(initialName);

  const [type, setType] = useState(tag?.type ?? defaultType ?? OrganisationTagType.EXTERNAL);

  const [followBehavior, setFollowBehavior] = useState(initialFollowBehavior);

  const onTypeChange = useCallback((value?: OrganisationTagType) => {
    if (!value) {
      return;
    }

    setType(value);
  }, []);

  const onFollowBehaviorChange = useCallback((value?: FeedFollowBehavior) => {
    if (!value) {
      return;
    }

    setFollowBehavior(value);
  }, []);

  const typeContent = useMemo(() => {
    if (tag) {
      return null;
    }

    return (
      <div className={classes['tag-edit__type']}>
        <Select
          label={t('common.type')}
          selectedItemId={type}
          items={Object.values(OrganisationTagType).map((value) => ({
            id: value,
            title: ctxT(`title-${value}`),
          }))}
          onChange={onTypeChange}
          staticListPosition
        />

        <div className={classes['tag-edit__type-hint']}>{ctxT('type-hint')}</div>
      </div>
    );
  }, [ctxT, onTypeChange, t, tag, type]);

  const followBehaviorSelect = useMemo(() => {
    if (type !== OrganisationTagType.EXTERNAL) {
      return null;
    }

    return (
      <Select
        label={t('common.feed-follow-behavior')}
        selectedItemId={followBehavior}
        items={Object.values(FeedFollowBehavior).map((value) => ({
          id: value,
          title: t(`feedFollowBehavior.${value}`),
          hint: t(`feedFollowBehaviorHints.${value}`),
        }))}
        onChange={onFollowBehaviorChange}
        staticListPosition
      />
    );
  }, [followBehavior, onFollowBehaviorChange, t, type]);

  const saveDisabled = useMemo(() => {
    if (!name.length) {
      return true;
    }

    if (!tag) {
      return false;
    }

    return name === initialName && followBehavior === initialFollowBehavior;
  }, [followBehavior, initialFollowBehavior, initialName, name, tag]);

  const onSubmitClick = useCallback(
    () => onSubmit({ name, type, feedFollowBehavior: followBehavior }),
    [followBehavior, name, onSubmit, type]
  );

  return (
    <div className={classes['tag-edit']}>
      <InputField
        value={name}
        placeholder={ctxT('add-placeholder')}
        onChange={({ target }) => setName(target.value)}
        maxLength={ORG_TAGS_NAME_LENGTH_LIMIT}
        autoFocus
      />

      {typeContent}

      {followBehaviorSelect}

      <Button
        type={ButtonType.primary}
        label={tag ? t('common.save') : ctxT('add-tag')}
        onClick={onSubmitClick}
        disabled={saveDisabled}
      />
    </div>
  );
};
